import type { QuoteRequestStatus } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { QUOTE_PROGRESS_STEPS, quoteProgressOf } from '@/entities/quote';
import { cn } from '@/shared/ui';

interface QuoteProgressStepsProps {
  readonly status: QuoteRequestStatus;
}

/**
 * 진행 단계 — 실제 상태가 있는 네 단계(견적 요청 → 검토 → 견적 발행 → 주문)만 굵은 선으로 잇는다.
 * 지나온 단계는 흑연, 지금 단계는 라임, 남은 단계는 옅은 선. 거절·반려·취소·만료면 멈춘 단계에서 끝났다고 적는다.
 */
export const QuoteProgressSteps = ({ status }: QuoteProgressStepsProps): ReactElement => {
  const { currentIndex, ended } = quoteProgressOf(status);

  return (
    <section aria-label="진행 단계" className="flex flex-col gap-3">
      <ol className="grid grid-cols-4 gap-1.5 sm:gap-2">
        {QUOTE_PROGRESS_STEPS.map((step, index) => {
          const isDone = index < currentIndex;
          const isCurrent = index === currentIndex;

          return (
            <li
              key={step}
              aria-current={isCurrent ? 'step' : undefined}
              className="flex flex-col gap-3"
            >
              <span
                aria-hidden
                className={cn(
                  'block h-1',
                  isDone && (ended ? 'bg-foreground/40' : 'bg-foreground'),
                  isCurrent && (ended ? 'bg-foreground/40' : 'bg-brand-lime'),
                  !isDone && !isCurrent && 'bg-foreground/10',
                )}
              />
              <span className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-2">
                <span aria-hidden className="text-[11px] text-muted-foreground tabular-nums">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span
                  className={cn(
                    'text-[13px] sm:text-sm',
                    isCurrent ? 'font-bold text-foreground' : undefined,
                    isDone ? 'text-foreground' : undefined,
                    !isDone && !isCurrent ? 'text-muted-foreground' : undefined,
                  )}
                >
                  {step}
                </span>
              </span>
            </li>
          );
        })}
      </ol>
      {ended ? (
        <p className="text-xs text-muted-foreground">이 견적은 이 단계에서 진행이 끝났습니다.</p>
      ) : null}
    </section>
  );
};
